import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import { FaPause, FaPlay } from "react-icons/fa";

const WarmupTimer = ({ onClose }) => {
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  const [isPaused, setIsPaused] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    let interval;
    if (!isPaused && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            onClose();
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPaused, timeLeft, onClose]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    if (timeLeft > 0) {
      setShowWarning(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    onClose();
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <motion.div
        className="relative bg-primary/90 p-8 rounded-2xl shadow-2xl border border-dark max-w-md w-full mx-4"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <IoMdClose size={24} />
        </button>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Warm-up Timer</h2>
          <p className="text-gray-400 mb-8">
            Take your time to warm up properly
          </p>

          <div className="text-6xl font-bold text-white mb-8 font-mono">
            {formatTime(timeLeft)}
          </div>

          <button
            onClick={togglePause}
            className="bg-secondary hover:bg-secondary/80 text-white px-6 py-3 rounded-lg flex items-center justify-center mx-auto transition-colors"
          >
            {isPaused ? (
              <FaPlay className="mr-2" />
            ) : (
              <FaPause className="mr-2" />
            )}
            {isPaused ? "Resume" : "Pause"}
          </button>
        </div>

        {/* Warning Modal */}
        <AnimatePresence>
          {showWarning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-60 flex items-center justify-center bg-black/60"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-primary p-6 rounded-xl shadow-xl max-w-sm mx-4"
              >
                <h3 className="text-xl font-bold text-white mb-4">Warning</h3>
                <p className="text-gray-300 mb-6">
                  Ending your warm-up early might affect your performance
                  statistics. Are you sure you want to continue?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowWarning(false)}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmClose}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    End Warm-up
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default WarmupTimer;
